import { create } from 'zustand';
import type {
	ChatConversationView,
	ChatMessageView,
	ChatParticipantView,
	ChatQueueEntryView,
} from './chat-types';

/**
 * Single source of truth for chat UI state. Populated from REST on
 * bootstrap and mutated in real time by MQTT inbox messages.
 *
 * Lives in libs/web-ui (not modules/chat) so Navigation.tsx and the
 * floating widget — both rendered inside libs/web-ui — can read and
 * dispatch to the same state that modules/chat (the /chat page) uses.
 */
interface ChatState {
	// Identity
	principalId: string | null;
	brokerUrl: string | null;
	/**
	 * Server-built MQTT topics the client uses. Every chat topic the
	 * client touches MUST come through this set — never reconstructed
	 * locally. Rationale: env scoping (`chat/prod/...` vs
	 * `chat/staging/...`) lives in one place on the server, and the
	 * authorizer's grant strings are built from the same source. If a
	 * client built its own topic string and the env-segment logic
	 * drifted, the broker would silently deny with no clear signal in
	 * production.
	 *
	 * `inboxTopic` — subscribe target for inbound messages.
	 * `outboxTopic` — publish target for messages, typing, reads.
	 * `presenceTopic` — publish target for the periodic heartbeat.
	 * `queueEventsTopic` / `availabilityEventsTopic` — internal-only
	 *   tenant-scoped event streams; null for portal principals.
	 */
	inboxTopic: string | null;
	outboxTopic: string | null;
	presenceTopic: string | null;
	queueEventsTopic: string | null;
	availabilityEventsTopic: string | null;

	/**
	 * Whether the current user is opted in to receive customer-support
	 * chats. Populated by ChatProvider after connect. Drives the queue
	 * pane visibility, the queue MQTT subscription gate, and the nav
	 * "Queue" link visibility — one source of truth for all three.
	 *
	 * `null` = not yet fetched (queue chrome stays hidden until known
	 * to avoid a flash of "available then disappears"). `true` /
	 * `false` = the answer.
	 */
	myCanReceiveCustomerChats: boolean | null;

	// Conversations
	conversations: Record<number, ChatConversationView>;
	participantsByConversation: Record<number, Array<ChatParticipantView>>;

	// Messages (keyed by conversationId)
	messagesByConversation: Record<number, Array<ChatMessageView>>;

	// Selection (for the full /chat page)
	activeConversationId: number | null;

	// Floating widget state (for pop-out windows on any page)
	widgetOpen: boolean;
	widgetConversationId: number | null;
	poppedOutConversationIds: Array<number>;

	// User-pinned conversations — surfaced at the top of the channel
	// list in a dedicated "Pinned" section. Persisted to localStorage
	// so pins survive reloads without a server round-trip.
	pinnedConversationIds: Array<number>;

	// User-hidden conversations — collapsed into a "Hidden" section at
	// the bottom of the channel list. Client-only preference (pure UI
	// decision, no server implication), persisted to localStorage.
	// Server-side mute (notificationsMuted on chat_participant) is
	// separate and lives on the participant row.
	hiddenConversationIds: Array<number>;

	// Unread counts per conversation. Server is the source of truth
	// (chat_read_state); this cache is seeded from /conversations and
	// incremented locally on inbox 'message' events for snappy UI.
	// Cleared when the conversation becomes active (markRead fires).
	unreadByConversationId: Record<
		number,
		{ unreadCount: number; unreadMentionCount: number }
	>;

	// Per-optimistic-message send failure, keyed by clientMessageId.
	// Cleared when the server fanout confirms the message or when the
	// user manually dismisses/retries.
	sendErrors: Record<string, string>;

	// Queue (for reps)
	queueEntries: Array<ChatQueueEntryView>;

	// Typing indicators — conversationId → set of typing principalIds
	typing: Record<number, Array<string>>;

	/**
	 * Internal-only availability cache, keyed by user.id. Drives the
	 * online dot on Avatar in DM rows + member drawers, and the
	 * caller's own status pill.
	 *
	 * Populated by:
	 *   - One bulk REST hydration on chat connect (every userId
	 *     already in `participantsByConversation`).
	 *   - The MQTT subscription on `chat/availability/{tenant}/events`
	 *     (every status change broadcast by the server).
	 *
	 * NEVER populated by polling. If a user appears in a chat we
	 * weren't tracking yet (e.g. they're added to a group while we
	 * watch), the next subscription update OR a refetch on
	 * `setParticipants` covers them.
	 *
	 * Customer-facing eligibility (`canReceiveCustomerChats`) is
	 * deliberately NOT mirrored here — it's an admin-controlled
	 * role assignment, not a reactive UI state. The status switcher
	 * shows the caller's own value via a one-shot REST read.
	 */
	availabilityByUserId: Record<
		number,
		{
			status: 'available' | 'away' | 'offline';
			message: string | null;
		}
	>;

	// Connection status. `connected` is the boolean the existing UI
	// binds against (legacy); `connectionState` + `connectionFailure`
	// are the richer fields the banner uses to differentiate
	// "Reconnecting…" (transient) from "Disconnected — retry" (we
	// gave up after N consecutive failures, or hit a terminal auth
	// rejection). Keeping both lets us migrate consumers gradually.
	connected: boolean;
	connectionState:
		| 'idle'
		| 'connecting'
		| 'connected'
		| 'reconnecting'
		| 'failed';
	connectionFailure: 'auth' | 'network' | 'unknown' | null;

	// Actions
	setIdentity: (identity: {
		principalId: string;
		brokerUrl: string;
		inboxTopic: string;
		outboxTopic: string;
		presenceTopic: string;
		queueEventsTopic: string | null;
		availabilityEventsTopic: string | null;
	}) => void;
	setMyCanReceiveCustomerChats: (value: boolean | null) => void;
	setConnected: (connected: boolean) => void;
	setConnectionState: (
		state: ChatStore['connectionState'],
		failure?: ChatStore['connectionFailure'],
	) => void;
	setConversations: (rows: Array<ChatConversationView>) => void;
	upsertConversation: (row: ChatConversationView) => void;
	setParticipants: (
		conversationId: number,
		members: Array<ChatParticipantView>,
	) => void;
	setActiveConversation: (id: number | null) => void;
	appendMessage: (conversationId: number, msg: ChatMessageView) => void;
	/**
	 * Bulk insert. Same dedup + chronological-sort semantics as
	 * `appendMessage` but mutates the store once and sorts once
	 * regardless of batch size — appendMessage in a loop would do
	 * N×O(N log N) work for each catch-up batch.
	 */
	appendMessages: (
		conversationId: number,
		msgs: ReadonlyArray<ChatMessageView>,
	) => void;
	prependHistory: (
		conversationId: number,
		msgs: Array<ChatMessageView>,
	) => void;
	setTyping: (
		conversationId: number,
		principalId: string,
		active: boolean,
	) => void;
	setQueueEntries: (entries: Array<ChatQueueEntryView>) => void;
	setSendError: (clientMessageId: string, error: string) => void;
	clearSendError: (clientMessageId: string) => void;
	openWidget: (conversationId?: number) => void;
	closeWidget: () => void;
	setWidgetConversation: (id: number | null) => void;
	popOut: (conversationId: number) => void;
	dismissPopOut: (conversationId: number) => void;
	pinConversation: (conversationId: number) => void;
	unpinConversation: (conversationId: number) => void;
	togglePinConversation: (conversationId: number) => void;
	hideConversation: (conversationId: number) => void;
	unhideConversation: (conversationId: number) => void;
	toggleHideConversation: (conversationId: number) => void;
	setUnreadMap: (
		map: Record<number, { unreadCount: number; unreadMentionCount: number }>,
	) => void;
	incrementUnread: (conversationId: number, isMention: boolean) => void;
	clearUnread: (conversationId: number) => void;

	/**
	 * Bulk-replace the availability cache. Used on initial hydration
	 * (one REST fetch when ChatProvider connects, populated from the
	 * users that already appear in `participantsByConversation`).
	 */
	setAvailabilityMap: (
		map: Record<
			number,
			{ status: 'available' | 'away' | 'offline'; message: string | null }
		>,
	) => void;
	/**
	 * Apply a single availability change. Called by the MQTT
	 * subscription on `chat/availability/{tenant}/events` for every
	 * `availability-changed` event. No polling — this is the only
	 * post-hydration write path.
	 */
	applyAvailabilityChange: (
		userId: number,
		status: 'available' | 'away' | 'offline',
		message: string | null,
	) => void;
}

const PINS_STORAGE_KEY = 'chat:pinnedConversationIds';
const HIDES_STORAGE_KEY = 'chat:hiddenConversationIds';

/**
 * Per-conversation soft cap on message-cache size. A long-running
 * session in a busy group can accumulate thousands of messages —
 * unbounded growth means the JS heap balloons and ChatThread's
 * re-render cost climbs O(N). 500 is well past any reasonable view
 * window; older messages page back in via `prependHistory` (which
 * does scroll-up REST fetches) when the user scrolls.
 */
const MESSAGE_CACHE_LIMIT = 500;

function capMessages<T>(rows: ReadonlyArray<T>): Array<T> {
	if (rows.length <= MESSAGE_CACHE_LIMIT) {
		return rows.slice();
	}
	// Keep the tail — newest messages. The user is on the latest
	// page; scroll-up backfill handles the head.
	return rows.slice(rows.length - MESSAGE_CACHE_LIMIT);
}

function loadIdsFromStorage(key: string): Array<number> {
	if (typeof window === 'undefined') return [];
	try {
		const raw = window.localStorage.getItem(key);
		if (raw == null || raw === '') return [];
		const parsed = JSON.parse(raw);
		if (!Array.isArray(parsed)) return [];
		return parsed
			.filter((v): v is number => typeof v === 'number' && Number.isFinite(v))
			.slice(0, 500);
	} catch {
		return [];
	}
}

function saveIdsToStorage(key: string, ids: Array<number>): void {
	if (typeof window === 'undefined') return;
	try {
		window.localStorage.setItem(key, JSON.stringify(ids));
	} catch {
		/* quota / disabled storage — harmless */
	}
}

export const useChatStore = create<ChatState>((set) => ({
	principalId: null,
	brokerUrl: null,
	inboxTopic: null,
	outboxTopic: null,
	presenceTopic: null,
	queueEventsTopic: null,
	availabilityEventsTopic: null,
	conversations: {},
	participantsByConversation: {},
	messagesByConversation: {},
	activeConversationId: null,
	widgetOpen: false,
	widgetConversationId: null,
	poppedOutConversationIds: [],
	pinnedConversationIds: loadIdsFromStorage(PINS_STORAGE_KEY),
	hiddenConversationIds: loadIdsFromStorage(HIDES_STORAGE_KEY),
	unreadByConversationId: {},
	sendErrors: {},
	queueEntries: [],
	typing: {},
	availabilityByUserId: {},
	connected: false,
	connectionState: 'idle',
	connectionFailure: null,
	myCanReceiveCustomerChats: null,

	setIdentity: (identity) => set(identity),
	setMyCanReceiveCustomerChats: (value) =>
		set({ myCanReceiveCustomerChats: value }),
	setConnected: (connected) => set({ connected }),
	setConnectionState: (connectionState, failure = null) =>
		set({
			connectionState,
			connectionFailure: connectionState === 'failed' ? failure : null,
			// Keep the legacy `connected` boolean in sync.
			connected: connectionState === 'connected',
		}),

	setConversations: (rows) =>
		set({ conversations: Object.fromEntries(rows.map((r) => [r.id, r])) }),
	upsertConversation: (row) =>
		set((state) => ({
			conversations: { ...state.conversations, [row.id]: row },
		})),

	setParticipants: (conversationId, members) =>
		set((state) => ({
			participantsByConversation: {
				...state.participantsByConversation,
				[conversationId]: members,
			},
		})),

	setActiveConversation: (id) => set({ activeConversationId: id }),

	appendMessage: (conversationId, msg) =>
		set((state) => {
			const existing = state.messagesByConversation[conversationId] ?? [];
			const key = msg.clientMessageId ?? msg.uuid;
			const filtered = existing.filter(
				(m) => (m.clientMessageId ?? m.uuid) !== key,
			);
			let nextErrors = state.sendErrors;
			if (
				msg.id >= 0 &&
				msg.clientMessageId != null &&
				nextErrors[msg.clientMessageId] != null
			) {
				const { [msg.clientMessageId]: _removed, ...rest } = nextErrors;
				nextErrors = rest;
			}
			// Insert chronologically. Out-of-order arrivals (catch-up
			// REST batch landing while live MQTT delivers a fresh
			// message, or a delayed fanout) would otherwise sit at the
			// end of the array regardless of when they were authored.
			// `createdDate` is ISO-8601 so lexical compare is
			// chronologically correct; for ties (same millisecond) we
			// fall back to `id` so server-confirmed rows out-rank
			// negative-id optimistic ones.
			const merged = [...filtered, msg].sort((a, b) => {
				const dateCmp = a.createdDate.localeCompare(b.createdDate);
				if (dateCmp !== 0) return dateCmp;
				return a.id - b.id;
			});
			return {
				messagesByConversation: {
					...state.messagesByConversation,
					[conversationId]: capMessages(merged),
				},
				sendErrors: nextErrors,
			};
		}),

	appendMessages: (conversationId, msgs) =>
		set((state) => {
			if (msgs.length === 0) return {};
			const existing = state.messagesByConversation[conversationId] ?? [];
			// Dedup by `clientMessageId ?? uuid` so optimistic rows are
			// replaced by their server-confirmed copy AND duplicate
			// MQTT/REST deliveries are no-ops.
			const byKey = new Map<string, ChatMessageView>();
			for (const m of existing) {
				byKey.set(m.clientMessageId ?? m.uuid, m);
			}
			for (const m of msgs) {
				byKey.set(m.clientMessageId ?? m.uuid, m);
			}
			let nextErrors = state.sendErrors;
			for (const m of msgs) {
				if (
					m.id >= 0 &&
					m.clientMessageId != null &&
					nextErrors[m.clientMessageId] != null
				) {
					const { [m.clientMessageId]: _r, ...rest } = nextErrors;
					nextErrors = rest;
				}
			}
			const merged = Array.from(byKey.values()).sort((a, b) => {
				const dateCmp = a.createdDate.localeCompare(b.createdDate);
				if (dateCmp !== 0) return dateCmp;
				return a.id - b.id;
			});
			return {
				messagesByConversation: {
					...state.messagesByConversation,
					[conversationId]: capMessages(merged),
				},
				sendErrors: nextErrors,
			};
		}),

	prependHistory: (conversationId, msgs) =>
		set((state) => {
			const existing = state.messagesByConversation[conversationId] ?? [];
			const byKey = new Map<string, ChatMessageView>();
			for (const m of [...msgs, ...existing]) {
				byKey.set(m.uuid, m);
			}
			return {
				messagesByConversation: {
					...state.messagesByConversation,
					[conversationId]: Array.from(byKey.values()).sort(
						(a, b) => a.id - b.id,
					),
				},
			};
		}),

	setTyping: (conversationId, principalId, active) =>
		set((state) => {
			const current = state.typing[conversationId] ?? [];
			const next = active
				? current.includes(principalId)
					? current
					: [...current, principalId]
				: current.filter((p) => p !== principalId);
			return { typing: { ...state.typing, [conversationId]: next } };
		}),

	setQueueEntries: (entries) => set({ queueEntries: entries }),

	setSendError: (clientMessageId, error) =>
		set((state) => ({
			sendErrors: { ...state.sendErrors, [clientMessageId]: error },
		})),
	clearSendError: (clientMessageId) =>
		set((state) => {
			if (state.sendErrors[clientMessageId] == null) return {};
			const { [clientMessageId]: _removed, ...rest } = state.sendErrors;
			return { sendErrors: rest };
		}),

	openWidget: (conversationId) =>
		set((state) => ({
			widgetOpen: true,
			widgetConversationId: conversationId ?? state.widgetConversationId,
		})),
	closeWidget: () => set({ widgetOpen: false }),
	setWidgetConversation: (id) => set({ widgetConversationId: id }),

	popOut: (conversationId) =>
		set((state) => {
			if (state.poppedOutConversationIds.includes(conversationId)) return {};
			return {
				poppedOutConversationIds: [
					...state.poppedOutConversationIds,
					conversationId,
				],
			};
		}),
	dismissPopOut: (conversationId) =>
		set((state) => ({
			poppedOutConversationIds: state.poppedOutConversationIds.filter(
				(id) => id !== conversationId,
			),
		})),

	pinConversation: (conversationId) =>
		set((state) => {
			if (state.pinnedConversationIds.includes(conversationId)) return {};
			const next = [...state.pinnedConversationIds, conversationId];
			saveIdsToStorage(PINS_STORAGE_KEY, next);
			return { pinnedConversationIds: next };
		}),
	unpinConversation: (conversationId) =>
		set((state) => {
			if (!state.pinnedConversationIds.includes(conversationId)) return {};
			const next = state.pinnedConversationIds.filter(
				(id) => id !== conversationId,
			);
			saveIdsToStorage(PINS_STORAGE_KEY, next);
			return { pinnedConversationIds: next };
		}),
	togglePinConversation: (conversationId) =>
		set((state) => {
			const isPinned = state.pinnedConversationIds.includes(conversationId);
			const next = isPinned
				? state.pinnedConversationIds.filter((id) => id !== conversationId)
				: [...state.pinnedConversationIds, conversationId];
			saveIdsToStorage(PINS_STORAGE_KEY, next);
			return { pinnedConversationIds: next };
		}),

	hideConversation: (conversationId) =>
		set((state) => {
			if (state.hiddenConversationIds.includes(conversationId)) return {};
			// Hiding implicitly unpins — a conversation cannot be in both.
			const pinned = state.pinnedConversationIds.filter(
				(id) => id !== conversationId,
			);
			const hidden = [...state.hiddenConversationIds, conversationId];
			saveIdsToStorage(PINS_STORAGE_KEY, pinned);
			saveIdsToStorage(HIDES_STORAGE_KEY, hidden);
			return {
				pinnedConversationIds: pinned,
				hiddenConversationIds: hidden,
			};
		}),
	unhideConversation: (conversationId) =>
		set((state) => {
			if (!state.hiddenConversationIds.includes(conversationId)) return {};
			const next = state.hiddenConversationIds.filter(
				(id) => id !== conversationId,
			);
			saveIdsToStorage(HIDES_STORAGE_KEY, next);
			return { hiddenConversationIds: next };
		}),
	toggleHideConversation: (conversationId) =>
		set((state) => {
			const isHidden = state.hiddenConversationIds.includes(conversationId);
			if (isHidden) {
				const next = state.hiddenConversationIds.filter(
					(id) => id !== conversationId,
				);
				saveIdsToStorage(HIDES_STORAGE_KEY, next);
				return { hiddenConversationIds: next };
			}
			const pinned = state.pinnedConversationIds.filter(
				(id) => id !== conversationId,
			);
			const hidden = [...state.hiddenConversationIds, conversationId];
			saveIdsToStorage(PINS_STORAGE_KEY, pinned);
			saveIdsToStorage(HIDES_STORAGE_KEY, hidden);
			return {
				pinnedConversationIds: pinned,
				hiddenConversationIds: hidden,
			};
		}),

	setUnreadMap: (map) => set({ unreadByConversationId: map }),

	incrementUnread: (conversationId, isMention) =>
		set((state) => {
			const curr = state.unreadByConversationId[conversationId] ?? {
				unreadCount: 0,
				unreadMentionCount: 0,
			};
			return {
				unreadByConversationId: {
					...state.unreadByConversationId,
					[conversationId]: {
						unreadCount: curr.unreadCount + 1,
						unreadMentionCount: curr.unreadMentionCount + (isMention ? 1 : 0),
					},
				},
			};
		}),

	clearUnread: (conversationId) =>
		set((state) => {
			if (state.unreadByConversationId[conversationId] == null) return {};
			const { [conversationId]: _removed, ...rest } =
				state.unreadByConversationId;
			return { unreadByConversationId: rest };
		}),

	setAvailabilityMap: (map) => set({ availabilityByUserId: map }),

	applyAvailabilityChange: (userId, status, message) =>
		set((state) => ({
			availabilityByUserId: {
				...state.availabilityByUserId,
				[userId]: { status, message },
			},
		})),
}));
